import React, { useEffect } from 'react';
import { usePermissionStore } from '../../store/usePermissionStore';
import { useAuthStore } from '../../store/useAuthStore';
import { LockedModule } from './LockedModule';
import { resolveModuleGuardView } from './moduleGuardState';
import { trackClarityEvent } from '../../lib/observability';

interface ModuleGuardProps {
  moduleCode: string;
  children: React.ReactNode;
}

export const ModuleGuard: React.FC<ModuleGuardProps> = ({ moduleCode, children }) => {
  const { isModuleEnabled, isLoaded, isLoading } = usePermissionStore();
  const { hasHydrated, isBootstrapped } = useAuthStore();

  const view = resolveModuleGuardView({
    hasHydrated,
    isBootstrapped,
    isLoading,
    isLoaded,
    moduleEnabled: isModuleEnabled(moduleCode),
  });

  // A locked module is a paywall/entitlement gate — surface it for Clarity funnels.
  const locked = view === 'locked';
  useEffect(() => {
    if (locked) {
      trackClarityEvent('module-locked-viewed', { module_code: moduleCode });
      trackClarityEvent('paywall-hit', { module_code: moduleCode });
    }
  }, [locked, moduleCode]);

  if (view === 'loading') {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600" />
          <p className="text-sm font-medium text-slate-500 animate-pulse">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  if (view === 'unavailable') {
    // Do NOT fail open: if module entitlements never loaded, show a retryable
    // error instead of silently rendering a gated module.
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
            We couldn't load your workspace permissions. Please try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  if (view === 'locked') {
    return <LockedModule moduleCode={moduleCode} />;
  }

  return <>{children}</>;
};
